import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RefSelect } from "@/components/data/FieldInput";
import { StatusBadge } from "@/components/data/StatusBadge";
import { api, errMsg, fmtDate, fmtMoney, today } from "@/lib/api";

const COMPTE = { name: "compte_id", type: "ref", ref: "comptes" };
const MOYEN = { name: "moyen_paiement_id", type: "refitem", ref: "moyen_paiement" };

export function PaiementDialog({ charge, onClose, onDone }) {
  const [f, setF] = useState({ montant: charge.reste, compte_id: null, moyen_paiement_id: null, date: today(), reference: "", commentaire: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/charges/${charge.id}/paiement`, f);
      toast.success("Paiement enregistré : le compte a été débité");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="charge-paiement-dialog">
        <DialogHeader>
          <DialogTitle>Paiement de la charge {charge.numero}</DialogTitle>
          <DialogDescription>Reste à payer : {fmtMoney(charge.reste)}. Seul ce paiement débite le compte choisi.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Montant *</Label><Input type="number" className="font-mono mt-1" value={f.montant} onChange={(e) => set("montant")(e.target.value)} data-testid="paiement-montant-input" /></div>
          <div><Label>Date *</Label><Input type="date" className="mt-1" value={f.date} onChange={(e) => set("date")(e.target.value)} data-testid="paiement-date-input" /></div>
          <div><Label>Compte *</Label><div className="mt-1"><RefSelect field={COMPTE} value={f.compte_id} onChange={set("compte_id")} testId="paiement-compte-select" /></div></div>
          <div><Label>Moyen de paiement *</Label><div className="mt-1"><RefSelect field={MOYEN} value={f.moyen_paiement_id} onChange={set("moyen_paiement_id")} testId="paiement-moyen-select" /></div></div>
          <div className="sm:col-span-2"><Label>Référence</Label><Input className="mt-1" value={f.reference} onChange={(e) => set("reference")(e.target.value)} data-testid="paiement-reference-input" /></div>
          <div className="sm:col-span-2"><Label>Commentaire</Label><Textarea className="mt-1" rows={2} value={f.commentaire} onChange={(e) => set("commentaire")(e.target.value)} data-testid="paiement-commentaire-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !f.montant || !f.compte_id || !f.moyen_paiement_id} data-testid="paiement-submit-button">{busy ? "Enregistrement…" : "Enregistrer le paiement"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaiementsList({ row }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/charges/${row.id}/paiements`).then((r) => setRows(r.data)).catch(() => {});
  }, [row.id]);
  return (
    <div className="mt-8" data-testid="charge-paiements-list">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wider">Suivi des paiements</h4>
      <div className="border rounded-md divide-y text-xs">
        {rows.map((p) => (
          <div key={p.id} className={`p-2.5 flex justify-between gap-2 ${p.statut === "annule" ? "opacity-55" : ""}`}>
            <span>{p.numero} · {fmtDate(p.date)} · {p.created_by_name}{p.reference ? ` · réf. ${p.reference}` : ""}</span>
            <span className="flex items-center gap-2"><StatusBadge value={p.statut} /><span className="font-mono font-semibold">{fmtMoney(p.montant)}</span></span>
          </div>
        ))}
        {!rows.length && <div className="p-3 text-muted-foreground">Aucun paiement enregistré</div>}
      </div>
    </div>
  );
}
